<template>
  <div class="UserEvents">
    <v-card outlined>
      <v-card-title class="subtitle-1">Eventos Importantes</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="newDescription"
          label="Descrição do evento"
          outlined
          dense
          hide-details
          maxlength="255"
        />
        <div class="d-flex align-center mt-2" style="gap:8px">
          <v-text-field
            v-model="newEventDate"
            type="datetime-local"
            label="Data do evento"
            outlined
            dense
            hide-details
          />
          <v-btn
            color="primary"
            :disabled="!newDescription.trim() || saving"
            :loading="saving"
            small
            @click="saveEvent"
          >
            Salvar
          </v-btn>
        </div>
      </v-card-text>

      <v-divider v-if="events.length" />

      <v-list dense v-if="events.length">
        <template v-for="event in events">
          <!-- Edit mode -->
          <v-list-item v-if="editingEventId === event.id" :key="`edit-${event.id}`" class="align-start py-2">
            <v-list-item-content>
              <v-text-field
                v-model="editingDescription"
                outlined
                dense
                hide-details
                maxlength="255"
                class="mb-2"
              />
              <div class="d-flex align-center mt-1" style="gap:8px">
                <v-text-field
                  v-model="editingEventDate"
                  type="datetime-local"
                  label="Data do evento"
                  outlined
                  dense
                  hide-details
                />
                <v-spacer />
                <v-btn text small @click="cancelEdit">Cancelar</v-btn>
                <v-btn
                  color="primary"
                  small
                  :disabled="!editingDescription.trim() || updating"
                  :loading="updating"
                  @click="updateEvent(event.id)"
                >
                  Salvar
                </v-btn>
              </div>
            </v-list-item-content>
          </v-list-item>

          <!-- Read mode -->
          <v-list-item v-else :key="event.id" class="align-start">
            <v-list-item-content>
              <v-list-item-subtitle class="caption">{{ formatDate(event.event_date) }}</v-list-item-subtitle>
              <v-list-item-title class="body-2 text-wrap">{{ event.description }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center" style="gap:4px">
              <v-btn icon small @click="startEdit(event)">
                <v-icon small color="grey">mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteEvent(event.id)">
                <v-icon small color="grey">mdi-delete-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </v-card>
  </div>
</template>

<script>
import UserEventService from '../../lib/spasms-control/UserEventService'
import { getToken } from '../../utils'
import store from '../../store'

export default {
  name: 'UserEvents',
  props: {
    dependentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      events: [],
      newDescription: '',
      newEventDate: this.nowDatetimeLocal(),
      saving: false,
      editingEventId: null,
      editingDescription: '',
      editingEventDate: '',
      updating: false
    }
  },
  methods: {
    nowDatetimeLocal() {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    toDatetimeLocal(isoStr) {
      const d = new Date(isoStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    loadEvents() {
      const service = new UserEventService(getToken())
      service.listEvents(this.dependentId).then(response => {
        this.events = response.data
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
      })
    },
    saveEvent() {
      this.saving = true
      const service = new UserEventService(getToken())
      const eventDateIso = new Date(this.newEventDate).toISOString()
      service.createEvent(this.dependentId, this.newDescription.trim(), eventDateIso).then(event => {
        this.events.unshift(event)
        this.newDescription = ''
        this.newEventDate = this.nowDatetimeLocal()
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao salvar evento')
        store.setShowSnackBarAction(true)
      }).finally(() => {
        this.saving = false
      })
    },
    startEdit(event) {
      this.editingEventId = event.id
      this.editingDescription = event.description
      this.editingEventDate = this.toDatetimeLocal(event.event_date)
    },
    cancelEdit() {
      this.editingEventId = null
      this.editingDescription = ''
      this.editingEventDate = ''
    },
    updateEvent(eventId) {
      this.updating = true
      const service = new UserEventService(getToken())
      const eventDateIso = new Date(this.editingEventDate).toISOString()
      service.updateEvent(eventId, this.editingDescription.trim(), eventDateIso).then(updated => {
        const idx = this.events.findIndex(e => e.id === eventId)
        if (idx !== -1) this.$set(this.events, idx, updated)
        this.cancelEdit()
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao atualizar evento')
        store.setShowSnackBarAction(true)
      }).finally(() => {
        this.updating = false
      })
    },
    deleteEvent(eventId) {
      const service = new UserEventService(getToken())
      service.deleteEvent(eventId).then(() => {
        this.events = this.events.filter(e => e.id !== eventId)
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao remover evento')
        store.setShowSnackBarAction(true)
      })
    },
    emitAnnotations() {
      const grouped = {}
      this.events.forEach(event => {
        const key = new Date(event.event_date).toLocaleDateString('pt-BR')
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(event.description)
      })
      const annotations = Object.entries(grouped).map(([date, texts]) => ({
        date,
        text: texts.join('\n─────\n')
      }))
      this.$emit('annotationsChanged', annotations)
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString('pt-BR')
    }
  },
  created() {
    this.loadEvents()
  }
}
</script>
